import { useCallback, useRef, useState } from 'react';

const DEFAULT_ROOT_MARGIN = '0px';
const DEFAULT_THRESHOLD = 0;

export interface UseIntersectionObserverArgs {
	rootMargin?: string;
	threshold?: number;
}

interface Intersection {
	isIntersecting: boolean;
}

type Node = HTMLElement | null;

export const useIntersectionObserver = ({ rootMargin = DEFAULT_ROOT_MARGIN, threshold = DEFAULT_THRESHOLD }: UseIntersectionObserverArgs = {}) => {
	const [intersection, setIntersection] = useState<Intersection>();

	const targetRef = useRef<Node>(null);
	const rootRef = useRef<Node>(null);
	const observerRef = useRef<IntersectionObserver | null>(null);

	const observe = useCallback(() => {
		const root = rootRef.current;
		const target = targetRef.current;

		if (!target) {
			return;
		}

		const options = { root, rootMargin: rootMargin, threshold };

		const observer = new IntersectionObserver(([{ isIntersecting }]) => {
			setIntersection({ isIntersecting });
		}, options);

		observer.observe(target);

		observerRef.current = observer;
	}, [rootMargin, threshold]);

	const unobserve = useCallback(() => {
		observerRef.current?.disconnect();
		observerRef.current = null;
	}, []);

	const initializeObserver = useCallback(() => {
		unobserve();
		observe();
	}, [observe, unobserve]);

	const rootRefCallback = useCallback(
		(node: Node) => {
			rootRef.current = node;
			initializeObserver();
		},
		[initializeObserver]
	);

	const targetCallback = useCallback(
		(node: Node) => {
			targetRef.current = node;
			initializeObserver();
		},
		[initializeObserver]
	);

	return {
		...intersection,
		rootRef: rootRefCallback,
		targetRef: targetCallback,
	};
};
